// Built-in admin tutorials. Videos are static files served from
// public/assets/help-videos (same location as the instructor tutorial).
const VIDEO_BASE = "/assets/help-videos";

export const INSTRUCTOR_VIDEO = `${VIDEO_BASE}/admin-add-instructor.mp4`;

export const ADMIN_GUIDES = [
  {
    _id: "guide-add-class",
    details: "Go to Menu → Class and click \"Add class\".\nEnter the Class Name and choose the Class In Charge.\nClick Save. The new class appears in the class list.\nOpen the class to set academic and transition dates or to add subjects.",
    question: "Add Class",
    answer: "Create a new class and set its class in-charge.",
    video: `${VIDEO_BASE}/admin-add-class.mp4`,
  },
  {
    _id: "guide-add-subject",
    details: "Go to Menu → Subject and click \"Add Subject\".\nEnter the Subject Name and select the Class and Department.\nClick Add Subject to save it.\nOpen the subject to assign an Instructor and Assistant Instructor.",
    question: "Add Subject",
    answer: "Add a subject to a class and department.",
    video: `${VIDEO_BASE}/admin-add-subject.mp4`,
  },
  {
    _id: "guide-add-instructor",
    details: "Go to Menu → Instructor.\nClick \"Add Instructor\".\nEnter the instructor's name, contact details and other information.\nSave to add the instructor, then assign them to classes and subjects.",
    question: "Add Instructor",
    answer: "Add a new instructor and assign them to classes and subjects.",
    video: INSTRUCTOR_VIDEO,
  },
  {
    _id: "guide-students",
    details: "Go to Menu → Student.\nBrowse the student list or search for a student by name.\nClick a student to open their profile and view their details.",
    question: "View Students",
    answer: "Browse the student list and open a student profile.",
    video: `${VIDEO_BASE}/admin-students.mp4`,
  },
  {
    _id: "guide-admissions",
    details: "Go to Menu → Admission.\nReview the list of admission applications.\nClick an application to view the applicant's profile and details.\nUpdate the application status to approve or process the admission.",
    question: "Admissions",
    answer: "Review admission applications and update their status.",
    video: `${VIDEO_BASE}/admin-admissions.mp4`,
  },
  {
    _id: "guide-exam",
    details: "Go to Menu → Exam.\nCreate a new exam or open an existing one from the list.\nSet the exam details such as class, subject and schedule.\nSave your changes to publish the exam.",
    question: "Exam",
    answer: "Create and manage exams for your classes.",
    video: `${VIDEO_BASE}/admin-exam.mp4`,
  },
  {
    _id: "guide-parent",
    details: "Go to Menu → Parent.\nBrowse or search the list of parents.\nClick a parent to view their details and linked students.",
    question: "View Parents",
    answer: "See parent details and their linked students.",
    video: `${VIDEO_BASE}/admin-parent.mp4`,
  },
  {
    _id: "guide-support-staff",
    details: "Go to Menu → Support Staff.\nBrowse the list of support staff members.\nClick a staff member to view or update their details.",
    question: "Support Staff",
    answer: "View and manage support staff members.",
    video: `${VIDEO_BASE}/admin-support-staff.mp4`,
  },
  {
    _id: "guide-course-board",
    details: "Go to Menu → Course Board.\nSelect the class and subject you want to view.\nReview the modules and syllabus planned for that subject.\nOpen a module to see its details and track progress.",
    question: "Course Board",
    answer: "Track modules and syllabus for each class and subject.",
    video: `${VIDEO_BASE}/admin-course-board.mp4`,
  },
  {
    _id: "guide-report-card",
    details: "Go to Menu → Report Card.\nCreate or choose a report card template for the class.\nGenerate report cards for the students of that class.\nOpen a student's report card to review or edit it before publishing.",
    question: "Report Card",
    answer: "Create templates and generate student report cards.",
    video: `${VIDEO_BASE}/admin-report-card.mp4`,
  },
  {
    _id: "guide-department",
    details: "Go to Menu → Department.\nClick \"Add Department\" and enter the department name.\nSave to create it.\nUse the edit and delete options on a department to update or remove it.",
    question: "Department",
    answer: "Create and manage school departments.",
    video: `${VIDEO_BASE}/admin-department.mp4`,
  },
  {
    _id: "guide-certificates",
    details: "Go to Menu → Certificate Builder.\nDesign or choose a certificate template.\nIssue the certificate to the required students.\nView or download the issued certificates.",
    question: "Certificates",
    answer: "Build certificate templates and issue them to students.",
    video: `${VIDEO_BASE}/admin-certificates.mp4`,
  },
  {
    _id: "guide-fee",
    details: "Go to Menu → Fee.\nCreate a fee template and assign it to classes.\nTrack fee payments and pending amounts.\nApprove transactions and discounts when required.",
    question: "Fee",
    answer: "Set up fee templates and track payments and discounts.",
    video: `${VIDEO_BASE}/admin-fee.mp4`,
  },
  {
    _id: "guide-class-announcements",
    details: "Go to Menu → Class Announcements.\nSelect the class you want to announce to.\nWrite the announcement and send it.\nView the announcements already sent.",
    question: "Class Announcements",
    answer: "Send announcements to a class and keep track of them.",
    video: `${VIDEO_BASE}/admin-class-announcements.mp4`,
  },
  {
    _id: "guide-schedule",
    details: "Go to Menu → Schedule.\nSelect the class and subject.\nSet the day and time for the sessions.\nSave to update the timetable.",
    question: "Schedule",
    answer: "Plan and view class and subject timetables.",
    video: `${VIDEO_BASE}/admin-schedule.mp4`,
  },
  {
    _id: "guide-alarm",
    details: "Go to Menu → Alarm.\nAdd a new alarm with its time and days.\nSave it so it rings on schedule.\nEdit or remove alarms when timings change.",
    question: "Alarm",
    answer: "Set up school bell alarms and timings.",
    video: `${VIDEO_BASE}/admin-alarm.mp4`,
  },
  {
    _id: "guide-student-attendance",
    details: "Go to Menu → Attendance.\nSelect the class and the date.\nMark each student as present or absent.\nSave to record the attendance and review it later.",
    question: "Student Attendance",
    answer: "Mark and review daily attendance for students.",
    video: `${VIDEO_BASE}/admin-student-attendance.mp4`,
  },
  {
    _id: "guide-staff-attendance",
    details: "Go to Menu → Instructor Attendance.\nSelect the date or period you want to view.\nReview each staff member's attendance status.\nOpen the report to see details and summaries.",
    question: "Staff Attendance",
    answer: "Track and review attendance for instructors and staff.",
    video: `${VIDEO_BASE}/admin-staff-attendance.mp4`,
  },
  {
    _id: "guide-messages",
    details: "Log into the Is-Kool portal and open the menu.\nUnder Communication, select Message.\nThe Messages page shows the chat history. Click Create New Message.\nIn the message form, select the class and then the student. The parent name is shown.\nType your message in the message box and click Send.\nYou can send more messages to the parent in the same way.",
    question: "Messages",
    answer: "Send a message to a student's parent.",
    video: `${VIDEO_BASE}/admin-messages.mp4`,
  },
  {
    _id: "guide-homework",
    details: "Log into the Is-Kool portal and open the menu.\nUnder Academic, select Homework.\nSelect the class. The class broadcast opens with the subjects on the left and the earlier messages.\nThe subject is set to All Subjects by default, or pick a particular subject.\nType the homework message in the message box. You can also attach a file or an image.\nClick Send. A message confirms that it was sent successfully.",
    question: "Homework",
    answer: "Send homework to a class.",
    video: `${VIDEO_BASE}/admin-homework.mp4`,
  },
];
